import { Options, useHotkeys } from 'react-hotkeys-hook';
import { useMapCanvas } from '../../../contexts/MapCanvasContext';
import { useMapEditorActions, useMapEditorSelection, useMapEditorUI, useMapEditorTool } from '../../../contexts/MapEditorContext';
import { useEditorShortcuts } from '../EditorShortcutContext/useEditorShortcuts';
import { GLOBAL_SHORTCUTS_SCOPE } from '../shortcuts';

export const useGlobalEditorShortcuts = () => {
  const {
    setActiveTool,
    copySelectedShapes,
    pasteShapes,
    undo,
    redo,
    setSelectedIds,
    groupSelected,
    ungroupShapes,
    setPreviewShape,
    handleDrawEnd,
    setDrawTool
  } = useMapEditorActions();
  const { selectedIds } = useMapEditorSelection();
  const { clipboard } = useMapEditorUI();
  const { activeTool } = useMapEditorTool();
  const { shapesRef } = useMapCanvas();
  const { shortcuts } = useEditorShortcuts();

  const enabled = true;
  const scopes = [GLOBAL_SHORTCUTS_SCOPE];
  const preventDefault = true;

  const shortcutOptions: Options = { scopes, enabled, preventDefault };

  useHotkeys(
    shortcuts.global.selectionTool,
    () => {
      setActiveTool('select');
    },
    shortcutOptions,
    [shortcuts.global.selectionTool]
  );

  useHotkeys(
    shortcuts.global.drawTool,
    () => {
      setDrawTool('rectangle');
      setActiveTool('draw');
    },
    shortcutOptions,
    [shortcuts.global.drawTool]
  );

  useHotkeys(
    shortcuts.global.circleTool,
    () => {
      setDrawTool('circle');
      setActiveTool('draw');
    },
    shortcutOptions,
    [shortcuts.global.circleTool]
  );

  useHotkeys(
    shortcuts.global.drawSelectTool,
    () => {
      setActiveTool('draw-select');
    },
    shortcutOptions,
    [shortcuts.global.drawSelectTool]
  );

  useHotkeys(
    shortcuts.global.iconTool,
    () => {
      // When shapes are selected, 'i' triggers eyedropper instead
      if (selectedIds.length > 0) return;
      setActiveTool('icon');
    },
    shortcutOptions,
    [shortcuts.global.iconTool, selectedIds]
  );

  useHotkeys(
    shortcuts.global.panTool,
    () => {
      setActiveTool('pan');
    },
    shortcutOptions,
    [shortcuts.global.panTool]
  );

  useHotkeys(
    shortcuts.global.wallPathTool,
    () => {
      setActiveTool('wall-path');
    },
    shortcutOptions,
    [shortcuts.global.wallPathTool]
  );

  useHotkeys(
    shortcuts.global.groupSelected,
    (event) => {
      if (selectedIds.length > 1) {
        event.preventDefault();
        groupSelected();
      }
    },
    shortcutOptions,
    [selectedIds, shortcuts.global.groupSelected]
  );

  useHotkeys(
    shortcuts.global.unGroupSelected,
    (event) => {
      shapesRef.current
        .filter((shape) => shape.type === 'group')
        .forEach((shape) => {
          event.preventDefault();
          ungroupShapes(shape.id);
        });
    },
    shortcutOptions,
    [shortcuts.global.unGroupSelected]
  );

  useHotkeys(
    shortcuts.global.copy,
    (event) => {
      // On Mac, ctrl+c triggers eyedropper when shapes are selected — skip copy
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      if (isMac && event.ctrlKey && !event.metaKey && selectedIds.length > 0) return;
      event.preventDefault();
      copySelectedShapes();
    },
    shortcutOptions,
    [shortcuts.global.copy, selectedIds]
  );

  useHotkeys(
    shortcuts.global.paste,
    (event) => {
      event.preventDefault();
      pasteShapes();
    },
    shortcutOptions,
    [shortcuts.global.paste, clipboard]
  );

  useHotkeys(
    shortcuts.global.undo,
    (event) => {
      event.preventDefault();
      undo();
    },
    shortcutOptions,
    [shortcuts.global.undo]
  );

  useHotkeys(
    shortcuts.global.redo,
    (event) => {
      event.preventDefault();
      redo();
    },
    shortcutOptions,
    [shortcuts.global.redo]
  );

  useHotkeys(
    shortcuts.global.selectAll,
    (event) => {
      event.preventDefault();
      setSelectedIds(
        shapesRef.current
          .filter((shape) => !shape.locked && !shape.hidden)
          .map((shape) => shape.id)
      );
    },
    shortcutOptions,
    [shortcuts.global.selectAll]
  );

  useHotkeys(
    shortcuts.global.deselectAll,
    (event) => {
      event.preventDefault();
      if (activeTool !== 'select') {
        setActiveTool('select');
        setPreviewShape(null);
        // Clear any in-progress draw/wall state
        handleDrawEnd({ x: 0, y: 0 }, { x: 0, y: 0 }, 'star');
      }
      setSelectedIds([]);
    },
    shortcutOptions,
    [shortcuts.global.deselectAll, activeTool]
  );
};
